import React from 'react'
import type { BodyRowConfig } from '@ztechapps/webui.data-tools'
import { CheckboxBodyCell } from '@ztechapps/webui.data-tools'
import { RowNumberBodyCell, ActivityListField } from '@ztechapps/webui.data-tools'
import { formatMoney } from '@ztechapps/common.utils'
import type { Product } from '@ztechapps/common.contract'
import { HorizontalStack, Switch } from '@ztechapps/webui.util'
import { Link } from 'react-router-dom'
import type { LinkToAltPathFunction } from './Context'
import { useProductListContext } from './Context'
import { ProductStatus } from '../ProductStatus/ProductStatus'
import { QuantityOnHand, RequiredComputedQuantityOnHand } from '../ProductQuantity'
import { ProductAvatar } from '../ProductImage/ProductAvatar'
import { ProductWidget } from '../ProductWidget'
import { Tags } from '@ztechapps/webui.input'
import type { EntityWithSelection } from '@ztechapps/common.hook'

export type ProductRenderDataBodyCellProps = {
  linkToAltPath?: LinkToAltPathFunction
} & BodyRowConfig<Product>

export const ProductRenderDataBodyCellConfig = (props: ProductRenderDataBodyCellProps) => {
  const {
    linkToAltPath,
    _id,
    code,
    name,
    status,
    price,
    coverImage,
    tags,
    inventory,
    requiredComputedQuantityOnHand,
    remarks,
    remarksActivity,
    coverImageThumb,
    baseUom,
    unitOfMeasurePrecision,
  } = props
  const linkTo = linkToAltPath ? linkToAltPath({ _id }) : ''
  const baseUomSymbol = baseUom?.symbol ?? ''
  const decimalPrecision = unitOfMeasurePrecision ?? 0

  return {
    code: (
      <div className="fw-bold">
        <Switch expression={!!linkTo}>
          {{
            true: <Link to={linkTo}>{code}</Link>,
            false: code,
          }}
        </Switch>
      </div>
    ),
    status: (
      <HorizontalStack gap={1}>
        <ProductStatus value={status} />
        <div>{status}</div>
      </HorizontalStack>
    ),
    price: <div className="text-end">{formatMoney(price)}</div>,
    quantityOnHand: (
      <div className="text-end">
        <QuantityOnHand
          quantityOnHand={inventory?.quantityOnHand}
          baseUomSymbol={baseUomSymbol}
          decimalPrecision={decimalPrecision}
        />
      </div>
    ),
    computedQuantity: (
      <div className="text-end">
        <RequiredComputedQuantityOnHand
          requiredComputedQuantityOnHand={requiredComputedQuantityOnHand}
          baseUomSymbol={baseUomSymbol}
          decimalPrecision={decimalPrecision}
        />
      </div>
    ),
    coverImage: <ProductAvatar name={name} coverImage={coverImage} coverImageThumb={coverImageThumb} />,
    tags: <Tags dataArray={tags} />,
    summary: (
      <ProductWidget
        {...{
          _id,
          name,
          coverImage,
          code,
          status,
          truncateSize: 200,
          linkTo,
        }}
      />
    ),
    remarks: <ActivityListField remarks={remarks} remarksActivity={remarksActivity} maxTruncate={200} />,
  }
}

export const ProductListRow = (props: BodyRowConfig<EntityWithSelection<Product>>) => {
  const { linkToAltPath, productListWithSelection } = useProductListContext()
  const productRenderDataBodyCellConfig = ProductRenderDataBodyCellConfig({ ...props, linkToAltPath })
  const { _id, _selected } = props
  const { toggleSelectBy } = productListWithSelection

  return (
    <>
      <RowNumberBodyCell {...props} />
      <CheckboxBodyCell
        checked={_selected}
        onChange={() => {
          toggleSelectBy((item) => item._id === _id)
        }}
      />
      {props._renderDataBodyCell(productRenderDataBodyCellConfig)}
    </>
  )
}
